import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  MoreVertical, 
  ChevronRight, 
  ChevronLeft, 
  Database, 
  LayoutGrid, 
  Users, 
  Workflow, 
  Columns3,
  Check,
  Loader2,
} from 'lucide-react';
import { getDevelopers, getStages, resetColumns } from '../../CachedData';

const options = [
  { key: "leads", label: "Lead list", icon: Database },
  { key: "savedViews", label: "Saved views", icon: LayoutGrid },
  { key: "users", label: "Team members", icon: Users },
  { key: "stages", label: "Pipeline stages", icon: Workflow },
  { key: "columns", label: "Column layout", icon: Columns3 },
];

const LeadsMenu = ({ workspaceId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [panel, setPanel] = useState('main'); // 'main' | 'reset'
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isResetting, setIsResetting] = useState(false);
  
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
    setTimeout(() => {
      setPanel('main');
      setSelectedKeys([]);
    }, 200);
  };

  const toggleSelection = (key) => {
    setSelectedKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    if (selectedKeys.length === options.length) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys(options.map(opt => opt.key));
    }
  };

  // Helper to remove every key starting with a specific prefix
  const clearKeysByPrefix = (prefix) => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  };

  const handleResetSelected = async () => {
    if (selectedKeys.length === 0) return;

    setIsResetting(true);

    try {
      // 1. Leads Data
      if (selectedKeys.includes('leads')) {
        clearKeysByPrefix('leadsData');
        window.dispatchEvent(new Event('leads-reset'));
      }

      // 2. Users Cache
      if (selectedKeys.includes('users')) {
        localStorage.removeItem('dev-users-cache');
        await getDevelopers(workspaceId);
      }

      // 3. Workflow Stages
      if (selectedKeys.includes('stages')) {
        localStorage.removeItem('lead_workflow-stages');
        await getStages();
      }

      // 4. Saved Views
      if (selectedKeys.includes('savedViews')) {
        localStorage.removeItem('lead_saved_views');
        window.dispatchEvent(new Event("saved-views-reset"));
      }

      // 5. Customized Columns
      if (selectedKeys.includes('columns')) {
        resetColumns();
        window.dispatchEvent(
          new Event("columns-reset")
        );
      }

      toast.success("Saved data refreshed.", {
        position: "top-center",
        autoClose: 2500,
      });

      closeMenu();

    } catch (err) {
      console.error("Cache Reset Error:", err);
      toast.error("Couldn’t refresh saved data. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="ws-dd-root" ref={menuRef}>
      <button
        type="button"
        onClick={() => (isOpen ? closeMenu() : setIsOpen(true))}
        className="crm-icon-btn"
        aria-label="More lead options"
        aria-haspopup="true"
        aria-expanded={isOpen}
        title="More"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <div className="ws-row-menu crm-leads-more" role="menu">
          {panel === 'main' && (
            <button type="button" role="menuitem" onClick={() => setPanel('reset')} className="ws-menu-item">
              <Database size={15} />
              <span className="ws-grow">Refresh saved data</span>
              <ChevronRight size={15} />
            </button>
          )}

          {panel === 'reset' && (
            <div>
              <div className="crm-leads-more-head">
                <button type="button" onClick={() => setPanel('main')} disabled={isResetting} className="crm-icon-btn crm-icon-btn--sm" aria-label="Back">
                  <ChevronLeft size={15} />
                </button>
                <span className="ws-h3">Refresh saved data</span>
              </div>
              <p className="ws-muted crm-leads-more-copy">Clears what this browser remembers and loads it again. Your leads are not changed.</p>
              <button type="button" onClick={handleSelectAll} disabled={isResetting} className="ws-btn ws-btn--plain crm-leads-more-all">
                {selectedKeys.length === options.length ? 'Clear selection' : 'Select everything'}
              </button>
              {options.map((opt) => {
                const Icon = opt.icon;
                const isSelected = selectedKeys.includes(opt.key);
                return (
                  <button
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={isSelected}
                    key={opt.key}
                    onClick={() => toggleSelection(opt.key)}
                    disabled={isResetting}
                    className="ws-menu-item"
                  >
                    <span className={`crm-check${isSelected ? ' is-on' : ''}`} aria-hidden="true">{isSelected && <Check size={11} strokeWidth={3} />}</span>
                    <Icon size={15} />
                    <span className="ws-grow">{opt.label}</span>
                  </button>
                );
              })}
              <div className="ws-menu-sep" />
              <button
                type="button"
                onClick={handleResetSelected}
                disabled={selectedKeys.length === 0 || isResetting}
                className="ws-btn ws-btn--primary ws-btn--sm"
                style={{ width: '100%' }}
              >
                {isResetting ? <><Loader2 size={14} className="ws-spin" />Refreshing…</> : `Refresh ${selectedKeys.length || ''} selected`.replace('  ', ' ')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadsMenu;